/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useEffect, useState } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import { PriceSourceName, UnitType } from "src/store/api/generatedApi";
import {
  buildMaterialColours,
  defaultLayout,
  generalTickFormatting,
  getLineDash,
  getMarkerSymbol,
  getMaterialColour,
  setSelectedDateFromPlotClick,
} from "../plotting";
import { GroupedPriceSeries } from "../types";
import { TFunction } from "i18next";
import { sorted } from "helpers";

interface PricesPlotProps {
  priceSeries: GroupedPriceSeries;
  selectedPriceSources: PriceSourceName[];
  selectedMaterials: string[];
  setSelectedDate: (date: Date) => void;
}

export const PricesPlot = ({
  priceSeries,
  selectedPriceSources,
  selectedMaterials,
  setSelectedDate,
}: PricesPlotProps) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);

  const [figure, setFigure] = useState<{
    data: Partial<PlotData>[];
    layout: Partial<Layout>;
  }>(() => ({
    data: [],
    layout: updateLayout(
      {
        ...defaultLayout,
        height: 700,
      },
      t,
      units
    ),
  }));

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- merges previous layout state; not purely computed
    setFigure({
      data: buildFigData(
        priceSeries,
        selectedPriceSources,
        selectedMaterials,
        t
      ),
      layout: updateLayout(figure.layout, t, units),
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- figure.layout intentionally excluded: effect calls setFigure, including it causes infinite loop
  }, [priceSeries, selectedPriceSources, selectedMaterials, t, units]);

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={figure.data}
        layout={figure.layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
      <LegendHint />
    </div>
  );
};

const buildFigData = (
  groupedPriceSeries: GroupedPriceSeries,
  selectedPriceSources: PriceSourceName[],
  selectedMaterials: string[],
  t: TFunction
): Partial<PlotData>[] => {
  const materialColours = buildMaterialColours(
    groupedPriceSeries.items.map((item) => ({
      groupingName: "Price Groups",
      groupName: item[0].materialName,
    }))
  );

  const sortedSeries = sorted(
    groupedPriceSeries.items.filter(
      ([key]) =>
        selectedPriceSources.includes(key.priceSource) &&
        selectedMaterials.includes(key.materialName)
    ),
    ([key]) => [
      groupedPriceSeries.availableMaterialNames.findIndex(
        (materialName) => materialName === key.materialName
      ) ?? -1,
      groupedPriceSeries.availablePriceSources.findIndex(
        (priceSource) => priceSource === key.priceSource
      ),
    ]
  );

  return sortedSeries.map(([key, series]) => {
    const colour = getMaterialColour(
      materialColours,
      "Price Groups",
      key.materialName
    );
    const lineDash = getLineDash(
      Array.from(groupedPriceSeries.availablePriceSources).indexOf(
        key.priceSource
      )
    );
    const markerSymbol = getMarkerSymbol(
      Array.from(groupedPriceSeries.availablePriceSources).indexOf(
        key.priceSource
      )
    );
    return {
      x: series.validFrom.map((validFrom) => validFrom.toISOString()),
      y: series.specificPrice,
      type: "scatter",
      mode: "lines+markers",
      visible: true,
      name: t(key.priceSource),
      legendgroup: key.materialName,
      legendgrouptitle: {
        text: key.materialName,
      },
      line: {
        color: colour,
        dash: lineDash,
        shape: "hv",
      },
      marker: {
        color: colour,
        symbol: markerSymbol,
      },
    };
  });
};

const updateLayout = (
  currentLayout: Partial<Layout>,
  t: (key: string) => string,
  units: (key: UnitType) => string
): Partial<Layout> => {
  const yaxisTitle = `${t("price")} ${units("specific_cost")}`;

  return {
    ...currentLayout,
    yaxis: {
      ...currentLayout.yaxis,
      title: { text: yaxisTitle },
      tickformat: generalTickFormatting,
    },
  };
};
